import type {
  RecordSearchResult,
  SessionHeapFieldValue,
  SessionHeapSnapshot,
} from "./types";

export function createEmptyHeapSnapshot(now = Date.now()): SessionHeapSnapshot {
  return {
    entriesByPath: {},
    listsByName: {},
    variablesByName: {},
    updatedAt: now,
  };
}

export function normalizeHeapSnapshot(raw: unknown): SessionHeapSnapshot {
  if (!raw || typeof raw !== "object") {
    return createEmptyHeapSnapshot();
  }

  const heap = raw as Partial<SessionHeapSnapshot>;
  return {
    entriesByPath:
      heap.entriesByPath && typeof heap.entriesByPath === "object"
        ? JSON.parse(JSON.stringify(heap.entriesByPath))
        : {},
    listsByName:
      heap.listsByName && typeof heap.listsByName === "object"
        ? JSON.parse(JSON.stringify(heap.listsByName))
        : {},
    variablesByName:
      heap.variablesByName && typeof heap.variablesByName === "object"
        ? JSON.parse(JSON.stringify(heap.variablesByName))
        : {},
    updatedAt: typeof heap.updatedAt === "number" ? heap.updatedAt : Date.now(),
  };
}

export function normalizeGraphPathSegment(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function extractRecordIdFromGraphPath(
  path: string,
  className: string,
): string {
  const normalizedPrefix = `${normalizeGraphPathSegment(className)}_`;
  if (path.startsWith(normalizedPrefix)) {
    return path.slice(normalizedPrefix.length);
  }
  const legacyPrefix = `${className}_`;
  if (path.startsWith(legacyPrefix)) {
    return path.slice(legacyPrefix.length);
  }
  return path;
}

export function toRecordSearchResult(
  className: string,
  node: Record<string, any>,
): RecordSearchResult | null {
  const path = typeof node.path === "string" ? node.path : "";
  if (!path) return null;
  const fields: SessionHeapFieldValue[] = Array.isArray(node.submodels)
    ? node.submodels.flatMap(
        (submodel: Record<string, any>): SessionHeapFieldValue[] => {
          const name =
            typeof submodel?.label === "string" && submodel.label.trim()
              ? submodel.label
              : typeof submodel?.path === "string"
                ? submodel.path.split(":").pop() || submodel.path
                : "";
          if (!name) return [];
          if (typeof submodel.string_value === "string") {
            return [{ name, type: "string", value: submodel.string_value }];
          }
          if (typeof submodel.number_value === "number") {
            return [{ name, type: "number", value: submodel.number_value }];
          }
          if (typeof submodel.boolean_value === "boolean") {
            return [
              {
                name,
                type: "boolean",
                value: submodel.boolean_value,
              },
            ];
          }
          return [];
        },
      )
    : [];
  const graphPathId = extractRecordIdFromGraphPath(path, className);
  const realIdField = fields.find(
    (field) =>
      normalizeGraphPathSegment(field.name) === "real_id" &&
      typeof field.value === "string" &&
      field.value.trim(),
  );
  const id =
    typeof realIdField?.value === "string"
      ? realIdField.value.trim()
      : graphPathId;
  const rawLabel =
    typeof node.label === "string" && node.label.trim() ? node.label : "";
  if (
    fields.length === 0 &&
    rawLabel &&
    isPlaceholderRecordLabel(rawLabel, graphPathId, path)
  ) {
    return null;
  }
  const fallbackLabel = displayLabelFromFields(fields);
  const label =
    rawLabel && !isPlaceholderRecordLabel(rawLabel, id, path)
      ? rawLabel
      : fallbackLabel || rawLabel || id;
  return {
    path,
    className,
    id,
    label,
    description:
      typeof node.description === "string" && node.description.trim()
        ? node.description
        : null,
    fields,
  };
}

function isPlaceholderRecordLabel(label: string, id: string, path: string) {
  const normalizedLabel = normalizeGraphPathSegment(label);
  return (
    normalizedLabel === normalizeGraphPathSegment(id) ||
    normalizedLabel === normalizeGraphPathSegment(path)
  );
}

function displayLabelFromFields(fields: SessionHeapFieldValue[]) {
  const preferredFieldNames = [
    "name",
    "title",
    "label",
    "display_name",
    "file_name",
    "number",
    "code",
  ];
  for (const preferred of preferredFieldNames) {
    const match = fields.find(
      (field) =>
        normalizeGraphPathSegment(field.name) === preferred &&
        typeof field.value === "string" &&
        field.value.trim(),
    );
    if (typeof match?.value === "string") {
      return match.value.trim();
    }
  }
  return null;
}

export function normalizeRecordSearchText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function rankRecordSearchResult(
  result: RecordSearchResult,
  query: string,
  index: number,
): number {
  const normalizedQuery = normalizeRecordSearchText(query);
  if (!normalizedQuery) {
    return index;
  }

  const label = normalizeRecordSearchText(result.label || "");
  const id = normalizeRecordSearchText(result.id || "");
  const path = normalizeRecordSearchText(result.path || "");
  const className = normalizeRecordSearchText(result.className || "");
  const searchable = [label, id, path, className].filter(Boolean);

  if (label === normalizedQuery) return index;
  if (id === normalizedQuery || path === normalizedQuery) return 100 + index;
  if (label.startsWith(normalizedQuery)) return 200 + index;
  if (searchable.some((value) => value.startsWith(normalizedQuery))) {
    return 300 + index;
  }
  if (label.includes(normalizedQuery)) return 400 + index;
  if (searchable.some((value) => value.includes(normalizedQuery))) {
    return 500 + index;
  }
  return 900 + index;
}

export function deriveRuntimeBaseUrl(apiEndpoint: string): string {
  try {
    const endpoint = new URL(apiEndpoint);
    const graphqlSuffix = "/orchestrator/graphql";
    if (endpoint.pathname.endsWith(graphqlSuffix)) {
      endpoint.pathname = endpoint.pathname.slice(0, -graphqlSuffix.length);
    } else if (endpoint.pathname.endsWith("/graphql")) {
      endpoint.pathname = endpoint.pathname.slice(0, -"/graphql".length);
    }
    endpoint.search = "";
    endpoint.hash = "";
    return endpoint.toString().replace(/\/$/, "");
  } catch {
    return apiEndpoint
      .replace(/\/orchestrator\/graphql$/, "")
      .replace(/\/$/, "");
  }
}
